import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api, ApiError } from '@/lib/api.ts'
import { useAuth } from '@/state/AuthContext.tsx'
import { Vine, LeafDivider, Seedling } from '@/components/Botanical.tsx'

/**
 * Reached from the link in the reset email.
 *
 * The link is checked before the form is shown, so nobody types out a new
 * password only to be told the link died twenty minutes ago.
 */
export default function ResetPassword() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const { completeReset } = useAuth()
  const navigate = useNavigate()

  const [checking, setChecking] = useState(true)
  const [valid, setValid] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!token) { setChecking(false); return }
    let cancelled = false
    api.auth
      .checkReset(token)
      .then(({ valid }) => { if (!cancelled) setValid(valid) })
      .catch(() => { if (!cancelled) setValid(false) })
      .finally(() => { if (!cancelled) setChecking(false) })
    return () => { cancelled = true }
  }, [token])

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const data = new FormData(event.currentTarget)
    const password = String(data.get('password') ?? '')
    const again = String(data.get('again') ?? '')
    if (password !== again) { setError('Those two do not match.'); return }

    setError(null)
    setBusy(true)
    try {
      await completeReset(token, password)
      navigate('/', { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.')
      setBusy(false)
    }
  }

  return (
    <div className="relative grid min-h-screen items-center overflow-hidden px-6 py-16">
      <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
        <Vine tone="var(--c-sage)" className="absolute -left-10 -top-10 h-[85vh] w-auto opacity-30 animate-sway" />
      </div>

      <div className="mx-auto w-full max-w-[27rem] animate-unfurl">
        <div className="text-center">
          <p className="eyebrow">Two to Three</p>
          <h1 className="mt-4 font-display text-[2.4rem] leading-[1.1]">
            {valid ? 'Choose a new password' : 'This link has expired'}
          </h1>
          <LeafDivider tone="var(--c-sage)" className="mx-auto mt-6 h-5 w-40 opacity-55" />
        </div>

        {checking ? (
          <p className="prose-soft mt-8 text-center">One moment…</p>
        ) : valid ? (
          <form onSubmit={submit} className="sheet mt-9 space-y-3.5 p-7">
            <label className="block">
              <span className="eyebrow">New password</span>
              <input name="password" type="password" required minLength={10} autoFocus
                autoComplete="new-password" placeholder="At least 10 characters" className="field mt-1.5" />
            </label>
            <label className="block">
              <span className="eyebrow">Once more</span>
              <input name="again" type="password" required minLength={10}
                autoComplete="new-password" placeholder="The same again" className="field mt-1.5" />
            </label>

            {error && (
              <p role="alert" className="rounded-leaf border border-clay/40 bg-blush/25 px-4 py-2.5 text-[0.85rem] text-terracotta">
                {error}
              </p>
            )}

            <button type="submit" disabled={busy} className="btn-primary mt-2 w-full">
              {busy ? 'Saving…' : 'Save it and come in'}
            </button>
            <p className="pt-1 text-center text-[0.76rem] text-whisper">
              This also signs out anywhere you were already signed in.
            </p>
          </form>
        ) : (
          <div className="mt-9 flex flex-col items-center gap-5 text-center">
            <Seedling tone="var(--c-clay)" className="h-16 w-auto opacity-50" />
            <p className="prose-soft max-w-[24rem]">
              Reset links only work once, and only for a little while. Ask for a fresh one and it
              will be along in a moment.
            </p>
            <Link to="/" className="btn-primary">Back to the door</Link>
          </div>
        )}
      </div>
    </div>
  )
}
